import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IssueEntity } from 'types/IssueEntity'
import { getShareNameAtPublicationHint } from './issueShareNameHint'
import InfoBlock from '../components/InfoBlock'
import { FactsTable, FactsTableRow } from '../components/FactsTable'
import Typography from '@mui/material/Typography'
import AdvisorList from './AdvisorList'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import useMediaQuery from '@mui/material/useMediaQuery'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'

type IssueInfoComponent = React.FC<{
    issue: IssueEntity
}>

export const IssueInfo: IssueInfoComponent = ({ issue }) => {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const xxlScreenOrBigger = useMediaQuery('(min-width:1900px)')

    if (!issue) {
        return <InfoBlock title={t('pages.issues.info.loadingTitle')} loadingElement={true} />
    }

    const shareNameHint = getShareNameAtPublicationHint(issue, t)
    const isEarnout = Boolean(issue.earnout && issue.type === 'Non-cash issue')
    const showExternalBlock = Boolean(shareNameHint || issue.externalComment || isEarnout)

    return (
        <InfoBlock title={t('pages.issues.info.title')}>
            <FactsTable>
                <FactsTableRow label={t('pages.issues.info.marketplace')}>
                    <Typography>{issue.marketplaceName}</Typography>
                </FactsTableRow>
                <FactsTableRow label={t('pages.issues.info.financialAdvisors')}>
                    {issue.noFinancialAdvisor ? (
                        '-'
                    ) : (
                        <AdvisorList advisors={issue.advisors} type='financial_advisor' />
                    )}
                </FactsTableRow>
                <FactsTableRow label={t('pages.issues.info.legalAdvisors')}>
                    {issue.noLegalAdvisor ? '-' : <AdvisorList advisors={issue.advisors} type='legal_advisor' />}
                </FactsTableRow>
                <FactsTableRow label={t('pages.issues.info.issuingAgent')}>
                    {issue.noIssuingAgent ? '-' : <AdvisorList advisors={issue.advisors} type='issuing_agent' />}
                </FactsTableRow>
            </FactsTable>
            {showExternalBlock ? (
                <>
                    <Divider />
                    {isEarnout ? (
                        <Typography
                            variant='body1'
                            component='p'
                            sx={{
                                whiteSpace: 'pre-line',
                                px: 4,
                                pt: 4,
                                pb: shareNameHint || issue.externalComment ? 0 : undefined,
                            }}
                        >
                            {t('pages.issues.comments.earnoutDescription')}
                        </Typography>
                    ) : null}
                    {shareNameHint ? (
                        <Typography
                            variant='body1'
                            component='p'
                            sx={{
                                fontStyle: 'italic',
                                whiteSpace: 'pre-line',
                                px: 4,
                                pt: isEarnout ? 2 : 4,
                            }}
                        >
                            {shareNameHint}
                        </Typography>
                    ) : null}
                    {issue.externalComment ? (
                        (issue.externalComment.length > 210 && !xxlScreenOrBigger) ||
                        (xxlScreenOrBigger && issue.externalComment.length > 400) ? (
                            <Stack
                                sx={{
                                    p: 4,
                                    pt: shareNameHint || isEarnout ? 2 : 4,
                                    pb: 0,
                                }}
                            >
                                <span style={{ whiteSpace: 'pre-line' }}>
                                    {isOpen
                                        ? issue.externalComment
                                        : `${issue.externalComment.slice(0, xxlScreenOrBigger ? 360 : 170)}...`}
                                </span>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Button
                                        variant='text'
                                        onClick={() => setIsOpen((prev) => !prev)}
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: '#FFF',
                                            },
                                        }}
                                    >
                                        {isOpen ? t('site.actions.showLess') : t('site.actions.showMore')}
                                    </Button>
                                </Box>
                            </Stack>
                        ) : (
                            <Box
                                sx={{
                                    p: 4,
                                    pt: shareNameHint || isEarnout ? 2 : 4,
                                    pb: 0,
                                }}
                            >
                                <span style={{ whiteSpace: 'pre-line' }}>{issue.externalComment}</span>
                            </Box>
                        )
                    ) : null}
                </>
            ) : null}
        </InfoBlock>
    )
}
